import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Globe, ChevronRight, MapPin, Building2 } from 'lucide-react';

const API_BASE = '';
const COUNTRY_IMAGES = {
  'US': 'https://images.unsplash.com/photo-1485738422979-f5c462d49f04?w=800&q=80',
  'MX': 'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?w=800&q=80',
  'CA': 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=800&q=80',
};

export default function WorldCupHostCountries() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/venues`).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  return (
    <div data-testid="wc-host-countries-page">
      <WCSubmenu />
      <SEOHead title={t('wc.hostCountries') + ' - ' + t('wc.breadcrumb') + ' | ODDSIS'} />

      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1a2744 100%)' }}>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Globe className="w-8 h-8 inline mr-3 text-amber-400" />{t('wc.hostCountries').toUpperCase()}
          </h1>
          <p className="text-sm text-slate-400">3 {t('wc.countriesLabel')} - 14 {t('wc.cities').toLowerCase()} - 14 {t('wc.stadiums').toLowerCase()}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-6">
        {data.host_countries?.map(country => (
          <div key={country.code} className="group relative overflow-hidden rounded-2xl border border-slate-800 hover:border-amber-500/30 transition-all" data-testid={`host-${country.code}`}>
            <div className="relative h-56 md:h-64 overflow-hidden">
              <img src={COUNTRY_IMAGES[country.code]} alt={country.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="flex items-center gap-3 mb-2">
                  <img src={country.flag} alt="" className="w-10 h-7 rounded shadow-lg" />
                  <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{country.name}</h2>
                </div>
                <p className="text-sm text-slate-300 max-w-2xl">{currentLang === 'tr' ? country.description_tr : country.description_en}</p>
              </div>
            </div>
            <div className="bg-slate-900 p-5 grid grid-cols-3 gap-4 border-t border-slate-800">
              <div className="text-center">
                <p className="text-2xl font-black text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{country.stadiums}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('wc.stadiums')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{country.matches}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('wc.matches')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-black text-amber-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{country.cities.length}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{t('wc.cities')}</p>
              </div>
            </div>
            <div className="bg-slate-950/50 px-5 py-3 flex flex-wrap gap-2">
              {country.cities.map(city => (
                <span key={city} className="text-xs bg-slate-800 text-slate-400 px-2.5 py-1 rounded-lg flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-amber-400/50" />{city}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
